import flatten from "lodash/flatten";
import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "react-query";

import type { GetRecommendationsResponse } from "@/react/api/recommendations/recommendationsApi";
import { recommendationsApi } from "@/react/api/recommendations/recommendationsApi";

const DEFAULT_PAGE_SIZE = 10;
const RECOMMENDATIONS_QUERY_KEY = "recommendations";

interface UseRecommendationsInfiniteQueryProps {
  initialPage?: number;
  options?: object;
  perPage?: number;
  scope?: string;
}

export const useRecommendationsInfiniteQuery = ({
  perPage = DEFAULT_PAGE_SIZE,
  initialPage = 1,
  options = {},
  scope = "default",
}: UseRecommendationsInfiniteQueryProps = {}) => {
  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    const params = {
      page,
      per_page: perPage,
    };

    return recommendationsApi.getRecommendations(params);
  };

  const queryKey = [RECOMMENDATIONS_QUERY_KEY, scope, perPage, initialPage];

  const queryOptions = {
    getNextPageParam: (lastPage: GetRecommendationsResponse) =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: GetRecommendationsResponse) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
    placeholderData: keepPreviousData,
    ...options,
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading,
    refetch,
  } = useInfiniteQuery({
    initialPageParam: initialPage,
    queryKey,
    queryFn: fetchPage,
    ...queryOptions,
  });
  const pages = useMemo(() => data?.pages || [], [data?.pages]);
  const count = pages[0]?.count || 0;
  const recommendations = useMemo(
    () => flatten(pages.map(page => page.records)),
    [pages],
  );

  return {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isInitialLoading,
    refetch,
    recommendations,
    count,
  };
};
